import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { connectDjpal, isDjpalTokenLive } from '../redux/thunk/djpalThunk';
import { clearToken } from '../redux/slices/djpalSlice';

// Cheap decode-only check, so a tight cadence costs nothing. An interval (not
// a one-shot timer at exp) so a Mac that slept past the deadline renews on
// wake, when suspended timers fire.
const CHECK_MS = 60_000;
// Renew while this much lifetime remains: small next to the token's 30 days,
// large enough that a failed exchange (offline blip) has many retries before
// the token actually dies.
const RENEW_MARGIN_MS = 10 * 60_000;
// Floor between exchange attempts while unhealthy. Each attempt can consume a
// /api/auth/refresh call, and that quota (60/hour/IP) is shared with the rest
// of the app — a 60s cadence alone could exhaust it during an auth outage.
const MIN_ATTEMPT_GAP_MS = 5 * 60_000;
// The exp check above is local-only: a token revoked server-side (or orphaned
// by a JWT_SECRET rotation) still looks live. Ask the auth service on this
// slow cadence so even that case heals in the background instead of only via
// an error surfacing on a Mixpedia search. Desktop-only: the web shim proxies
// this path into music-info-server, which doesn't host it.
const VALIDATE_GAP_MS = 60 * 60_000;
const IS_DESKTOP = typeof window !== 'undefined' && !!window.electron?.db;

/**
 * App-level DJ Pal token renewal. Mount ONCE, unconditionally.
 *
 * The DJ Pal token is a 30-day JWT that plenty of code reads straight from
 * localStorage['djpal:token'] (feed, MusicPlayer, likes, lazy resolvers).
 * useDjpalConnect establishes it but lives only in the Mixpedia-family tabs —
 * and once tokenValid === true its loop stops for good, so nothing there
 * re-checks a token that ages past its exp inside a session left open for
 * days. This hook watches the token's own exp from the app shell: inside the
 * renewal margin it drops the cache and runs the exchange itself, keeping the
 * localStorage readers fresh even in sessions that never opened a Mixpedia
 * tab (default landing is the home grid, activeTab null).
 */
export default function useDjpalTokenRenewal() {
    const dispatch = useDispatch();
    const isAuthenticated = useSelector((s) => s.auth.isAuthenticated);
    const reduxToken = useSelector((s) => s.djpal.token);
    const lastAttemptRef = useRef(0);
    const lastValidateRef = useRef(0);

    useEffect(() => {
        if (!isAuthenticated) return undefined;
        const renewIfNearExpiry = () => {
            const token = reduxToken || localStorage.getItem('djpal:token');
            // Attempt whenever there's no LIVE token: missing (pre-warms the
            // exchange at login, and keeps retrying if a renewal attempt
            // failed offline), expired, or inside the renewal margin. A live
            // token means ~30 days of quiet ticks.
            if (isDjpalTokenLive(token, RENEW_MARGIN_MS)) {
                validateInBackground(token);
                return;
            }
            const now = Date.now();
            if (now - lastAttemptRef.current < MIN_ATTEMPT_GAP_MS) return;
            lastAttemptRef.current = now;
            // Drop the dying cache first — connectDjpal trusts any cached
            // token whose exp hasn't elapsed, so renewing early requires the
            // cache to be gone before the exchange.
            if (token) dispatch(clearToken());
            dispatch(connectDjpal());
        };
        const validateInBackground = (token) => {
            if (!IS_DESKTOP) return;
            const now = Date.now();
            if (now - lastValidateRef.current < VALIDATE_GAP_MS) return;
            lastValidateRef.current = now;
            window.electron.djpal.validateToken(token).then((r) => {
                // Act ONLY on an explicit server verdict — a transport error
                // comes back as {error} and must never wipe a working token.
                if (!r || r.valid !== false) return;
                // The token may have been renewed while the request was in
                // flight; only drop what we actually validated.
                if (localStorage.getItem('djpal:token') !== token) return;
                dispatch(clearToken());
                dispatch(connectDjpal());
            }).catch(() => { /* transport failure — keep the token */ });
        };
        renewIfNearExpiry();
        const t = setInterval(renewIfNearExpiry, CHECK_MS);
        return () => clearInterval(t);
    }, [isAuthenticated, reduxToken, dispatch]);
}
